/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { Bookmark, BookmarkCheck } from 'lucide-react';
import { useBookmark } from '@/components/bookmark/useBookmark';

const ICON_SIZE = { sm: 'h-4 w-4', md: 'h-[18px] w-[18px]' };
const TEXT_CLASS = {
  on: 'bg-primary/10 text-primary',
  off: 'text-muted-foreground hover:bg-secondary/60',
};
const ICON_CLASS = {
  on: 'text-primary bg-primary/10',
  off: 'text-muted-foreground hover:text-foreground hover:bg-secondary/60',
};

/**
 * Universal bookmark button - works for posts, resources and initiatives.
 * Optimistic update, persistent state via /api/bookmarks endpoints.
 *
 * Props:
 *   targetType: 'post' | 'resource' | 'initiative'
 *   targetId:   string
 *   initialSaved?: boolean (if known from parent - avoids the initial GET)
 *   size?: 'sm' | 'md' (default 'md')
 *   variant?: 'icon' | 'text' (default 'icon')
 *   onChange?: (saved: boolean) => void
 */
const BookmarkButton = ({ targetType, targetId, initialSaved, size = 'md', variant = 'icon', onChange }) => {
  const { saved, loading, toggle, t } = useBookmark({ targetType, targetId, initialSaved, onChange });
  const Icon = saved ? BookmarkCheck : Bookmark;
  const iconSize = ICON_SIZE[size] || ICON_SIZE.md;
  const label = saved ? t('unsave') : t('save');
  const tone = saved ? 'on' : 'off';
  const shared = {
    onClick: toggle,
    disabled: loading,
    'data-testid': `bookmark-btn-${targetType}-${targetId}`,
    'aria-pressed': saved,
  };

  if (variant === 'text') {
    return (
      <button {...shared} aria-label={label} className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-lg px-2.5 py-1.5 transition-colors ${TEXT_CLASS[tone]}`}>
        <Icon className={iconSize} />
        {saved ? t('saved') : t('save')}
      </button>
    );
  }
  return (
    <button {...shared} title={label} className={`inline-flex items-center justify-center rounded-lg h-8 w-8 transition-colors ${ICON_CLASS[tone]}`}>
      <Icon className={iconSize} />
    </button>
  );
};

export default BookmarkButton;
